import React, { useRef, useEffect, useState } from 'react';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 400;
const PADDLE_WIDTH = 100;
const PADDLE_HEIGHT = 10;
const BALL_SIZE = 10;
const PADDLE_SPEED = 5;
const BALL_SPEED = 3;

function App() {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [paused, setPaused] = useState(false);

  const paddleX = useRef(CANVAS_WIDTH / 2 - PADDLE_WIDTH / 2);
  const ballX = useRef(CANVAS_WIDTH / 2);
  const ballY = useRef(CANVAS_HEIGHT / 2);
  const ballDX = useRef(BALL_SPEED);
  const ballDY = useRef(BALL_SPEED);
  const scoreRef = useRef(0);
  const keysPressed = useRef({});
  const rafId = useRef(null);
  const touchButtons = useRef({ left: false, right: false });
  const pausedRef = useRef(false);

  const togglePause = () => {
    pausedRef.current = !pausedRef.current;
    setPaused(pausedRef.current);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const draw = () => {
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      ctx.fillStyle = '#1a1a2e';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Dotted centre line
      ctx.setLineDash([6, 6]);
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath();
      ctx.moveTo(0, CANVAS_HEIGHT / 2);
      ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT / 2);
      ctx.stroke();
      ctx.setLineDash([]);

      // Paddle
      ctx.fillStyle = '#e94560';
      ctx.beginPath();
      ctx.roundRect(paddleX.current, CANVAS_HEIGHT - PADDLE_HEIGHT - 10, PADDLE_WIDTH, PADDLE_HEIGHT, 4);
      ctx.fill();

      // Ball
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.arc(ballX.current, ballY.current, BALL_SIZE / 2, 0, Math.PI * 2);
      ctx.fill();

      // Score
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 20px Arial';
      ctx.fillText(`Score: ${scoreRef.current}`, 10, 30);

      // Paused overlay
      if (pausedRef.current) {
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 48px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('⏸ PAUSED', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 10);
        ctx.font = '18px Arial';
        ctx.fillStyle = '#aaa';
        ctx.fillText('Press Space or P to resume', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 32);
        ctx.textAlign = 'left';
      }
    };

    const update = () => {
      // Keyboard + on-screen button movement
      if ((keysPressed.current['ArrowLeft'] || touchButtons.current.left) && paddleX.current > 0) {
        paddleX.current = Math.max(0, paddleX.current - PADDLE_SPEED * 2);
      }
      if ((keysPressed.current['ArrowRight'] || touchButtons.current.right) && paddleX.current < CANVAS_WIDTH - PADDLE_WIDTH) {
        paddleX.current = Math.min(CANVAS_WIDTH - PADDLE_WIDTH, paddleX.current + PADDLE_SPEED * 2);
      }

      ballX.current += ballDX.current;
      ballY.current += ballDY.current;

      if (ballX.current <= BALL_SIZE / 2) {
        ballX.current = BALL_SIZE / 2;
        ballDX.current = Math.abs(ballDX.current);
      } else if (ballX.current >= CANVAS_WIDTH - BALL_SIZE / 2) {
        ballX.current = CANVAS_WIDTH - BALL_SIZE / 2;
        ballDX.current = -Math.abs(ballDX.current);
      }

      if (ballY.current <= BALL_SIZE / 2) {
        ballY.current = BALL_SIZE / 2;
        ballDY.current = Math.abs(ballDY.current);
      }

      const paddleTop = CANVAS_HEIGHT - PADDLE_HEIGHT - 10;
      if (
        ballDY.current > 0 &&
        ballY.current + BALL_SIZE / 2 >= paddleTop &&
        ballY.current - BALL_SIZE / 2 <= paddleTop + PADDLE_HEIGHT &&
        ballX.current >= paddleX.current &&
        ballX.current <= paddleX.current + PADDLE_WIDTH
      ) {
        ballY.current = paddleTop - BALL_SIZE / 2;
        ballDY.current = -Math.abs(ballDY.current);
        const hitPos = (ballX.current - paddleX.current) / PADDLE_WIDTH;
        ballDX.current = BALL_SPEED * (hitPos - 0.5) * 2;
        scoreRef.current += 1;
        setScore(scoreRef.current);
      }

      if (ballY.current > CANVAS_HEIGHT + BALL_SIZE) {
        ballX.current = CANVAS_WIDTH / 2;
        ballY.current = CANVAS_HEIGHT / 2;
        ballDX.current = BALL_SPEED * (Math.random() > 0.5 ? 1 : -1);
        ballDY.current = BALL_SPEED;
        scoreRef.current = 0;
        setScore(0);
      }
    };

    const gameLoop = () => {
      if (!pausedRef.current) update();
      draw();
      rafId.current = requestAnimationFrame(gameLoop);
    };

    rafId.current = requestAnimationFrame(gameLoop);

    // --- Keyboard ---
    const handleKeyDown = (e) => {
      if (e.key === ' ' || e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        pausedRef.current = !pausedRef.current;
        setPaused(pausedRef.current);
        return;
      }
      keysPressed.current[e.key] = true;
      if (e.key.startsWith('Arrow')) e.preventDefault();
    };
    const handleKeyUp = (e) => { keysPressed.current[e.key] = false; };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // --- Touch drag on canvas: move paddle to finger position ---
    const getCanvasX = (clientX) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = CANVAS_WIDTH / rect.width;
      return (clientX - rect.left) * scaleX;
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const cx = getCanvasX(touch.clientX);
      paddleX.current = Math.min(
        CANVAS_WIDTH - PADDLE_WIDTH,
        Math.max(0, cx - PADDLE_WIDTH / 2)
      );
    };

    canvas.addEventListener('touchstart', handleTouchMove, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      cancelAnimationFrame(rafId.current);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      canvas.removeEventListener('touchstart', handleTouchMove);
      canvas.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  // On-screen button handlers
  const btnStyle = (active) => ({
    width: 80,
    height: 60,
    fontSize: 28,
    borderRadius: 12,
    border: 'none',
    background: active ? '#e94560' : 'rgba(233,69,96,0.3)',
    color: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    touchAction: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  });

  const startBtn = (dir) => (e) => {
    e.preventDefault();
    touchButtons.current[dir] = true;
  };
  const stopBtn = (dir) => (e) => {
    e.preventDefault();
    touchButtons.current[dir] = false;
  };

  return (
    <div style={{
      textAlign: 'center',
      fontFamily: 'Arial, sans-serif',
      background: '#0f0f1a',
      minHeight: '100vh',
      padding: '12px 8px',
      color: '#fff',
      boxSizing: 'border-box',
    }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 'clamp(20px, 5vw, 32px)' }}>🏓 Pong</h1>
      <p style={{ margin: '0 0 10px', color: '#aaa', fontSize: 'clamp(12px, 3vw, 16px)' }}>
        Drag on canvas • Buttons below • ← → keys
      </p>

      {/* Responsive canvas */}
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        style={{
          border: '2px solid #e94560',
          borderRadius: 6,
          display: 'block',
          margin: '0 auto',
          width: '100%',
          maxWidth: 800,
          height: 'auto',
          touchAction: 'none',
        }}
      />

      {/* On-screen left/right buttons + pause */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 24,
        marginTop: 18,
      }}>
        <button
          style={btnStyle(touchButtons.current.left)}
          onPointerDown={startBtn('left')}
          onPointerUp={stopBtn('left')}
          onPointerLeave={stopBtn('left')}
        >◀</button>
        <button
          onClick={togglePause}
          style={{
            width: 72,
            height: 60,
            fontSize: 26,
            borderRadius: 12,
            border: '2px solid rgba(233,69,96,0.6)',
            background: paused ? '#e94560' : 'rgba(233,69,96,0.15)',
            color: '#fff',
            cursor: 'pointer',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s',
          }}
          title="Pause / Resume (Space or P)"
        >{paused ? '▶' : '⏸'}</button>
        <button
          style={btnStyle(touchButtons.current.right)}
          onPointerDown={startBtn('right')}
          onPointerUp={stopBtn('right')}
          onPointerLeave={stopBtn('right')}
        >▶</button>
      </div>

      <p style={{ marginTop: 10, color: '#666', fontSize: 12 }}>Score: {score}</p>
    </div>
  );
}

export default App;
